import React, { useState, useEffect, useRef } from 'react';
import { X, ChevronDown, Menu } from 'react-feather';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import '../App.css';

// Import events data to get all event IDs for the dropdown
import eventsData from '../data/eventsData';

// Import Google Fonts
import '@fontsource/pirata-one';
import '@fontsource/merriweather-sans';

const defaultNavLinks = [
  { 
    name: 'Home', 
    href: '#home', 
    isHash: true, 
    exact: false,
    // Special handling for home to ensure it's only active when at root or when #home is in the URL
    isActive: (currentLocation) => 
      currentLocation.pathname === '/' || 
      currentLocation.pathname === '' || 
      currentLocation.hash === '#home'
  },
  { name: 'About', href: '#about', isHash: true },
  { name: 'Events', href: '#events', isHash: true },
  { name: 'Schedule', href: 'https://drive.google.com/file/d/1HwZtP4e_15ss8OHVNrsz2e0UGWcd7Gf7/view?usp=drive_link', isHash: false, isExternal: true },
  { name: 'Coordinators', href: '#coordinators', isHash: true },
  { name: 'Sponsors', href: '#sponsors', isHash: true },
];

const eventPageNavLinks = [
  { 
    name: 'More Events',
    isDropdown: true,
    items: eventsData.map(event => ({
      name: event.subtitle,
      href: `/events/${event.id}`,
      isHash: false
    })),
    isHash: false
  },
  { name: 'Home', href: '/', isHash: false }
];

const Navbar = ({ isEventPage = false }) => {
  const navLinks = isEventPage ? eventPageNavLinks : defaultNavLinks;
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);
  const location = useLocation();
  const [scrolledPastHero, setScrolledPastHero] = useState(false);
  
  const toggleDropdown = (index) => {
    setActiveDropdown(activeDropdown === index ? null : index);
  };
  
  const isDropdownActive = (index) => activeDropdown === index;

  const isActive = (href, exact = false, isHash = false) => {
    if (isHash) {
      // For hash links, check both the current hash and if we're at the home page
      return location.hash === href && (location.pathname === '/' || location.pathname === '');
    }
    if (exact) {
      // For exact matches (like home), only return true if we're exactly at that path
      return location.pathname === href || (href === '/' && location.pathname === '');
    }
    // For non-exact matches, check if the current path starts with the href
    return location.pathname !== '/' && location.pathname.startsWith(href);
  };

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      const heroSection = document.getElementById('home');
      const heroHeight = heroSection ? heroSection.offsetHeight : 0;
      setScrolledPastHero(window.scrollY > heroHeight * 0.8);
      setIsScrolled(window.scrollY > 10);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menu when clicking outside or pressing Escape
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target) && 
          buttonRef.current && !buttonRef.current.contains(event.target)) {
        closeMenu();
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        closeMenu();
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'auto';
    };
  }, [isMenuOpen]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const navVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.5,
        ease: 'easeOut'
      }
    }
  };

  const linkVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: (i) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: 0.1 * i,
        duration: 0.3
      }
    })
  };

  return (
    <>
      <motion.nav 
        className={`navbar navbar-expand-lg navbar-dark ${isScrolled || scrolledPastHero ? 'bg-dark shadow-lg' : 'bg-transparent'} fixed-top py-0`}
        aria-label="Main navigation"
        initial="hidden"
        animate="visible"
        variants={navVariants}
      >
        <div className="container">
          {/* Brand/logo */}
          <Link to="/" className="navbar-brand d-flex align-items-center" style={{ fontFamily: '"Pirata One", cursive' }}>
            <motion.img 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }} 
              src={process.env.PUBLIC_URL + '/assets/college_logo.png'} 
              alt="College Logo" 
              className="navbar-logo me-1"
              style={{ height: '70px', width: 'auto' }}
            />
          </Link>

          {/* Mobile menu button */}
          <motion.button
            ref={buttonRef}
            className="navbar-toggler p-2"
            type="button"
            onClick={toggleMenu}
            aria-expanded={isMenuOpen}
            aria-label="Toggle navigation"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            {isMenuOpen ? (
              <X size={28} className="text-white" />
            ) : (
              <Menu size={28} className="text-white" />
            )}
          </motion.button>

          {/* Desktop Navigation */}
          <div className="collapse navbar-collapse" id="navbarNav">
            <ul className="navbar-nav ms-auto mb-2 mb-lg-0">
              {navLinks.map((link, index) => (
                <motion.li 
                  key={index} 
                  className="nav-item position-relative"
                  custom={index}
                  variants={linkVariants}
                  initial="hidden"
                  animate="visible"
                >
                  {link.isDropdown ? (
                    <div className="dropdown">
                      <motion.div 
                        className="nav-link dropdown-toggle d-flex align-items-center" 
                        onClick={() => toggleDropdown(index)}
                        whileHover={{ color: '#ffd700' }}
                      >
                        {link.name}
                        <motion.span 
                          animate={{ rotate: isDropdownActive(index) ? 180 : 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          <ChevronDown size={18} className="ms-1" />
                        </motion.span>
                      </motion.div>
                      <div className="dropdown-menu">
                        {link.items.map((item) => (
                          <Link 
                            key={item.href}
                            to={item.href}
                            className="dropdown-item"
                            onClick={closeMenu}
                          >
                            {item.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  ) : link.isExternal ? (
                    <a 
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="nav-link position-relative"
                      onClick={closeMenu}
                    >
                      <span className="nav-link-text">
                        {link.name}
                      </span>
                      <span className="nav-underline" style={{ width: 0, backgroundColor: 'transparent' }} />
                    </a>
                  ) : link.isHash ? (
                    <a 
                      href={link.href} 
                      className={`nav-link position-relative ${location.hash === link.href ? 'active' : ''}`}
                      onClick={closeMenu}
                    >
                      <span className="nav-link-text">
                        {link.name}
                      </span>
                      <span 
                        className="nav-underline"
                        style={{
                          width: isActive(link.href, false, true) ? '100%' : 0,
                          backgroundColor: isActive(link.href, false, true) ? '#ffd700' : 'transparent'
                        }}
                      />
                    </a>
                  ) : (
                    <Link 
                      to={link.href} 
                      className="nav-link position-relative"
                      onClick={closeMenu}
                    >
                      <span className="nav-link-text">
                        {link.name}
                        <span 
                          className="nav-underline"
                          style={{
                            width: link.isActive ? (link.isActive(location) ? '100%' : 0) : (isActive(link.href, link.exact, link.isHash) ? '100%' : 0),
                            backgroundColor: link.isActive ? (link.isActive(location) ? '#ffd700' : 'transparent') : (isActive(link.href, link.exact, link.isHash) ? '#ffd700' : 'transparent')
                          }}
                        />
                      </span>
                    </Link>
                  )}
                </motion.li>
              ))}
            </ul>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu Overlay */}
      <div className={`mobile-menu-overlay d-lg-none ${isMenuOpen ? 'active' : ''}`}>
        <div className="mobile-menu-container" ref={menuRef}>
          <div className="mobile-menu-header">
            <span className="menu-title">MENU</span>
            <button 
              className="close-button" 
              onClick={closeMenu}
              aria-label="Close menu"
            >
              <X size={28} />
            </button>
          </div>
          
          <nav className="mobile-nav">
            <ul className="mobile-nav-list">
              {navLinks.map((link, index) => (
                <li key={link.name} className="mobile-nav-item" style={{ '--i': index + 1 }}>
                  {link.isDropdown ? (
                    <div className={`mobile-dropdown ${isDropdownActive(index) ? 'active' : ''}`}>
                      <div 
                        className="mobile-dropdown-header"
                        onClick={() => toggleDropdown(index)}
                      >
                        <span className="mobile-nav-link">{link.name}</span>
                        <ChevronDown 
                          size={20} 
                          className={`ms-2 transition-transform ${isDropdownActive(index) ? 'rotate-180' : ''}`}
                        />
                      </div>
                      <ul className="mobile-dropdown-list">
                        {link.items.map((item, itemIndex) => (
                          <li 
                            key={item.href} 
                            className="mobile-dropdown-item"
                            style={{
                              transitionDelay: `${itemIndex * 0.05}s`,
                              opacity: isDropdownActive(index) ? 1 : 0,
                              transform: isDropdownActive(index) ? 'translateX(0)' : 'translateX(-10px)'
                            }}
                          >
                            <Link 
                              to={item.href} 
                              className="mobile-dropdown-link"
                              onClick={() => {
                                closeMenu();
                                setActiveDropdown(null);
                              }}
                            >
                              {item.name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    link.isHash ? (
                      <a 
                        href={link.href} 
                        className="mobile-nav-link"
                        onClick={() => {
                          closeMenu();
                          setActiveDropdown(null);
                        }}
                      >
                        {link.name}
                      </a>
                    ) : (
                      <Link 
                        to={link.href} 
                        className="mobile-nav-link"
                        onClick={() => {
                          closeMenu();
                          setActiveDropdown(null);
                        }}
                      >
                        {link.name}
                      </Link>
                    )
                  )}
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </div>
    </>
  );
};

// Add some custom styles for the navbar
const styles = `
  .nav-underline {
    position: absolute;
    bottom: 0;
    left: 0;
    height: 2px;
    display: block;
    transition: width 0.3s ease, background-color 0.3s ease;
  }
  
  .navbar {
    transition: all 0.3s ease-in-out;
    background: linear-gradient(90deg, rgba(0,0,0,0.9) 0%, rgba(20,20,20,0.95) 100%) !important;
    backdrop-filter: blur(10px);
    border-bottom: 1px solid rgba(255, 215, 0, 0.2);
  }
  
  .navbar-brand {
    font-size: 1.8rem;
    font-weight: 700;
    letter-spacing: 1px;
    text-shadow: 2px 2px 4px rgba(0,0,0,0.5);
  }
  
  .nav-link {
    font-family: 'Merriweather Sans', sans-serif;
    font-weight: 500;
    padding: 0.8rem 1.2rem !important;
    margin: 0 0.1rem;
    position: relative;
    overflow: hidden;
    transition: all 0.3s ease;
    color: rgba(255, 255, 255, 0.9) !important;
    outline: none !important;
    box-shadow: none !important;
  }
  
  .nav-link:hover, .nav-link:focus {
    color: #ffd700 !important;
    transform: translateY(-2px);
    outline: none !important;
    box-shadow: none !important;
  }
  
  .nav-link-text {
    position: relative;
    display: inline-block;
  }
  
  .dropdown-menu {
    background: rgba(30, 30, 30, 0.98);
    border: 1px solid rgba(255, 215, 0, 0.2);
    border-radius: 8px;
    padding: 0.5rem 0;
    margin-top: 0.5rem;
    box-shadow: 0 10px 30px rgba(0,0,0,0.3);
  }
  
  .dropdown-item {
    color: #fff;
    padding: 0.5rem 1.5rem;
    transition: all 0.2s ease;
  }
  
  .dropdown-item:hover {
    background: rgba(255, 215, 0, 0.1);
    color: #ffd700;
    transform: translateX(5px);
  }
  
  @media (max-width: 991.98px) {
    .navbar-collapse {
      background: rgba(0, 0, 0, 0.95);
      padding: 1rem;
      border-radius: 0 0 10px 10px;
      box-shadow: 0 10px 30px rgba(0,0,0,0.3);
    }
    
    .nav-link {
      padding: 0.8rem 0 !important;
      margin: 0.3rem 0;
    }
  }
`;

// Add styles to the document head
const styleElement = document.createElement('style');
styleElement.textContent = styles;
document.head.appendChild(styleElement);

export default Navbar;
